/**
 * Form Field Validators
 * Supports Taiwan-specific formats: ID, phone, etc.
 */

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

// Taiwan ID (身分證字號) validator
// Format: 1 letter + 9 digits, with checksum validation
export function validateTaiwanId(value: string): ValidationResult {
  if (!value) return { valid: true }; // Empty is handled by required check

  const id = value.toUpperCase().trim();

  // Basic format check: 1 letter + 9 digits
  if (!/^[A-Z][12]\d{8}$/.test(id)) {
    return { valid: false, message: '身分證格式錯誤（應為1個英文字母+9個數字）' };
  }

  // Letter to number mapping for Taiwan ID
  const letterMap: Record<string, number> = {
    A: 10, B: 11, C: 12, D: 13, E: 14, F: 15, G: 16, H: 17, I: 34,
    J: 18, K: 19, L: 20, M: 21, N: 22, O: 35, P: 23, Q: 24, R: 25,
    S: 26, T: 27, U: 28, V: 29, W: 32, X: 30, Y: 31, Z: 33
  };

  const letterValue = letterMap[id[0]];
  if (!letterValue) {
    return { valid: false, message: '身分證字母無效' };
  }

  // Checksum calculation
  const n1 = Math.floor(letterValue / 10);
  const n2 = letterValue % 10;

  let sum = n1 + n2 * 9;
  for (let i = 1; i < 9; i++) {
    sum += parseInt(id[i]) * (9 - i);
  }
  sum += parseInt(id[9]);

  if (sum % 10 !== 0) {
    return { valid: false, message: '身分證號碼檢查碼錯誤' };
  }

  return { valid: true };
}

// Taiwan Resident Certificate (居留證) validator
// Old format: 2 letters + 8 digits (e.g., AB12345678)
// New format (2020+): Same as Taiwan ID but second digit is 8 or 9
export function validateResidentCertificate(value: string): ValidationResult {
  if (!value) return { valid: true };

  const id = value.toUpperCase().trim();

  // Area code mapping (same as Taiwan ID)
  const areaCodeMap: Record<string, number> = {
    A: 10, B: 11, C: 12, D: 13, E: 14, F: 15, G: 16, H: 17, I: 34,
    J: 18, K: 19, L: 20, M: 21, N: 22, O: 35, P: 23, Q: 24, R: 25,
    S: 26, T: 27, U: 28, V: 29, W: 32, X: 30, Y: 31, Z: 33
  };

  // Check for new format (2020+): 1 letter + 8 or 9 + 8 digits
  if (/^[A-Z][89]\d{8}$/.test(id)) {
    const letterValue = areaCodeMap[id[0]];
    if (!letterValue) {
      return { valid: false, message: '居留證區域代碼無效' };
    }

    // Checksum calculation (same as Taiwan ID)
    const n1 = Math.floor(letterValue / 10);
    const n2 = letterValue % 10;

    let sum = n1 + n2 * 9;
    for (let i = 1; i < 9; i++) {
      sum += parseInt(id[i]) * (9 - i);
    }
    sum += parseInt(id[9]);

    if (sum % 10 !== 0) {
      return { valid: false, message: '居留證號碼檢查碼錯誤' };
    }

    return { valid: true };
  }

  // Check for old format: 2 letters + 8 digits
  if (/^[A-Z]{2}\d{8}$/.test(id)) {
    // First letter should be valid area code
    if (!areaCodeMap[id[0]]) {
      return { valid: false, message: '居留證區域代碼無效' };
    }

    // Second letter should be A-H (old format gender codes)
    // A-D for males, E-H for females
    if (!/^[A-H]$/.test(id[1])) {
      return { valid: false, message: '居留證性別代碼無效（應為A-H）' };
    }

    return { valid: true };
  }

  return { valid: false, message: '居留證格式錯誤（應為2個英文字母+8個數字，或新式1個英文字母+8或9+8個數字）' };
}

// Taiwan phone number validator
// Mobile: 09XX-XXX-XXX (10 digits starting with 09)
// Landline: 0X-XXXX-XXXX (9-10 digits starting with 0)
export function validateTaiwanPhone(value: string): ValidationResult {
  if (!value) return { valid: true };

  // Remove all non-digit characters for validation
  const digits = value.replace(/\D/g, '');

  // Mobile phone: starts with 09, 10 digits total
  if (digits.startsWith('09')) {
    if (digits.length !== 10) {
      return { valid: false, message: '手機號碼應為10位數字（09開頭）' };
    }
    return { valid: true };
  }

  // Landline: starts with 0, 9-10 digits
  if (digits.startsWith('0')) {
    if (digits.length < 9 || digits.length > 10) {
      return { valid: false, message: '市話號碼應為9-10位數字' };
    }
    return { valid: true };
  }

  return { valid: false, message: '電話號碼應以0開頭' };
}

// Mobile phone only validator (stricter)
export function validateMobilePhone(value: string): ValidationResult {
  if (!value) return { valid: true };

  const digits = value.replace(/\D/g, '');

  if (!digits.startsWith('09')) {
    return { valid: false, message: '手機號碼應以09開頭' };
  }

  if (digits.length !== 10) {
    return { valid: false, message: '手機號碼應為10位數字' };
  }

  return { valid: true };
}

// Email validator
export function validateEmail(value: string): ValidationResult {
  if (!value) return { valid: true };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (!emailRegex.test(value.trim())) {
    return { valid: false, message: 'Email 格式錯誤' };
  }

  return { valid: true };
}

// Date validator (age check)
export function validateBirthDate(value: string, minAge?: number, maxAge?: number): ValidationResult {
  if (!value) return { valid: true };

  const birthDate = new Date(value);
  const today = new Date();

  if (isNaN(birthDate.getTime())) {
    return { valid: false, message: '日期格式錯誤' };
  }

  if (birthDate > today) {
    return { valid: false, message: '出生日期不能是未來日期' };
  }

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  if (minAge !== undefined && age < minAge) {
    return { valid: false, message: `年齡需滿 ${minAge} 歲` };
  }

  if (maxAge !== undefined && age > maxAge) {
    return { valid: false, message: `年齡不可超過 ${maxAge} 歲` };
  }

  return { valid: true };
}

// Generic required field validator
export function validateRequired(value: any): ValidationResult {
  if (value === undefined || value === null || value === '') {
    return { valid: false, message: '此欄位為必填' };
  }
  return { valid: true };
}

// Min/Max length validator
export function validateLength(value: string, min?: number, max?: number): ValidationResult {
  if (!value) return { valid: true };

  if (min !== undefined && value.length < min) {
    return { valid: false, message: `至少需要 ${min} 個字元` };
  }

  if (max !== undefined && value.length > max) {
    return { valid: false, message: `不可超過 ${max} 個字元` };
  }

  return { valid: true };
}

// Pattern validator (custom regex)
export function validatePattern(value: string, pattern: string, message?: string): ValidationResult {
  if (!value) return { valid: true };

  const regex = new RegExp(pattern);
  if (!regex.test(value)) {
    return { valid: false, message: message || '格式錯誤' };
  }

  return { valid: true };
}

// Number range validator
export function validateNumberRange(value: number | string, min?: number, max?: number): ValidationResult {
  if (value === '' || value === undefined || value === null) return { valid: true };

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return { valid: false, message: '請輸入有效數字' };
  }

  if (min !== undefined && num < min) {
    return { valid: false, message: `數值不可小於 ${min}` };
  }

  if (max !== undefined && num > max) {
    return { valid: false, message: `數值不可大於 ${max}` };
  }

  return { valid: true };
}

// Unified field validator based on field type and validation config
export interface FieldValidationConfig {
  type: string;
  required?: boolean;
  validation?: {
    pattern?: string;
    patternMessage?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    minAge?: number;
    maxAge?: number;
    format?: 'taiwan_id' | 'resident_cert' | 'mobile' | 'phone' | 'email';
  };
}

export function validateField(value: any, config: FieldValidationConfig): ValidationResult {
  // Required check
  if (config.required) {
    const requiredResult = validateRequired(value);
    if (!requiredResult.valid) return requiredResult;
  }

  // Skip other validations if value is empty
  if (!value) return { valid: true };

  const stringValue = String(value);

  // Type-specific validation
  switch (config.type) {
    case 'email':
      return validateEmail(stringValue);

    case 'tel':
      // Check for specific format
      if (config.validation?.format === 'mobile') {
        return validateMobilePhone(stringValue);
      }
      return validateTaiwanPhone(stringValue);

    case 'taiwan_id':
      return validateTaiwanId(stringValue);

    case 'resident_cert':
      return validateResidentCertificate(stringValue);

    case 'date':
      if (config.validation?.minAge !== undefined || config.validation?.maxAge !== undefined) {
        return validateBirthDate(stringValue, config.validation.minAge, config.validation.maxAge);
      }
      break;

    case 'number':
      return validateNumberRange(value, config.validation?.min, config.validation?.max);
  }

  // Custom format validation
  if (config.validation?.format) {
    switch (config.validation.format) {
      case 'taiwan_id':
        return validateTaiwanId(stringValue);
      case 'resident_cert':
        return validateResidentCertificate(stringValue);
      case 'mobile':
        return validateMobilePhone(stringValue);
      case 'phone':
        return validateTaiwanPhone(stringValue);
      case 'email':
        return validateEmail(stringValue);
    }
  }

  // Pattern validation
  if (config.validation?.pattern) {
    const patternResult = validatePattern(
      stringValue,
      config.validation.pattern,
      config.validation.patternMessage
    );
    if (!patternResult.valid) return patternResult;
  }

  // Length validation
  if (config.validation?.minLength || config.validation?.maxLength) {
    const lengthResult = validateLength(
      stringValue,
      config.validation.minLength,
      config.validation.maxLength
    );
    if (!lengthResult.valid) return lengthResult;
  }

  return { valid: true };
}

export default {
  validateTaiwanId,
  validateResidentCertificate,
  validateTaiwanPhone,
  validateMobilePhone,
  validateEmail,
  validateBirthDate,
  validateRequired,
  validateLength,
  validatePattern,
  validateNumberRange,
  validateField,
};
